// src/components/SchedulePane.jsx
// 授業スケジュール（曜日時限・教室）【2026-09-28 ClassDetailPage から切り出して作り直し】
//
//   ・曜日時限は「時限」を選ぶと講時の表（教務管理の「講時の時刻」）から時刻が入る。
//     講時に合わない授業は、時刻を直接入れてもよい。
//   ・教室は「授業の教室」（基本）と、曜日時限ごとの教室の2段。曜日時限の教室が
//     空なら授業の教室を使う（月3は A 教室・木2は B 教室、に対応するため）。
//   ・【2026-09-28 見直し】ふだんは「現在の設定」を読むだけの表で見せる。
//     直すときは行の「変更」（その曜日時限だけ）か、上の「まとめて変更…」（選んだ曜日時限）。
//     教室（基本）も同じカードの中で見せて直す。
//   ・「自動で出席を開始」（class_schedules.auto_attendance）【2026-09-28】
//     ふだんは授業ごとにまとめてオン／オフ。特定の曜日時限だけ止めたいときは「詳細設定」から外す。
//     （曜日時限ごとの設定をあちこちで触れるようにすると、どこがオンか分からなくなるため）
//     実際に動くのは「システム全体（教務）」「アカウント設定」「この曜日時限」の3つがオンのとき。
//     どれかがオフで動かないときは、その理由を画面に出す。
//   サーバー: /api/schedule/schedules/:id（送った項目だけ変わる）, /api/schedule/classes/:id（授業の教室）
import React, { useEffect, useMemo, useState } from 'react';
import UpcomingSessions from './UpcomingSessions';
import { confirmDialog } from './ConfirmDialog';
import CopyFromPreviousCard from './CopyFromPreviousCard';

const DAY_NAMES = ['日', '月', '火', '水', '木', '金', '土'];
const hm = (t) => (t ? String(t).slice(0, 5) : '');

export default function SchedulePane({ classId, cls, auth, reload, setErr, setMsg, schedules, sessions, students }) {
  const [periods, setPeriods] = useState([]);   // [{ periodNo, label, startTime, endTime }]
  const [drafts, setDrafts]   = useState({});   // 行ごとの書きかけ { [id]: {...} }
  const [editingId, setEditingId] = useState(null);   // 個別に直している曜日時限
  const [panel, setPanel]     = useState(null);       // null / 'bulk' / 'add' / 'room'
  const [picked, setPicked]   = useState([]);   // まとめて変更の対象
  const [bulk, setBulk]       = useState({ period: '', room: '', clearRoom: false, late: '' });
  const [showAutoDetail, setShowAutoDetail] = useState(false);   // 自動出席の詳細設定
  const [autoInfo, setAutoInfo] = useState(null);   // { enabled（自分）, systemEnabled（全体） }
  const [classRoom, setClassRoom] = useState((cls && cls.room) || '');
  const [busy, setBusy]       = useState(false);
  const [add, setAdd] = useState({ day: 1, period: '', start: '09:15', end: '10:45', room: '', late: 15 });

  useEffect(() => { setClassRoom((cls && cls.room) || ''); }, [cls && cls.room]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setDrafts({}); setEditingId(null); }, [schedules]);
  const loadAutoInfo = async () => {
    try {
      const r = await fetch('/api/user/auto-attendance', { headers: auth() });
      const d = await r.json();
      if (d.success) setAutoInfo({ enabled: d.enabled, systemEnabled: d.systemEnabled });
    } catch (e) { /* 取れなければ案内を出さないだけ */ }
  };
  useEffect(() => { loadAutoInfo(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** アカウント設定の自動出席をオンにする（この画面から直せるように） */
  const enableMyAuto = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/user/auto-attendance', { method: 'PUT', headers: auth(), body: JSON.stringify({ enabled: true }) });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || '変更できませんでした');
      setMsg('アカウント設定の自動出席をオンにしました');
      loadAutoInfo();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/periods', { headers: auth() });
        const d = await r.json();
        if (d.success) setPeriods(d.periods || []);
      } catch (e) { /* 講時が引けなくても時刻の直接入力はできる */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const periodOf = (start, end) => {
    const p = periods.find(x => x.startTime === hm(start) && (!end || x.endTime === hm(end)));
    return p ? String(p.periodNo) : '';
  };
  const periodTimes = (no) => periods.find(x => String(x.periodNo) === String(no)) || null;

  // 行の今の値（書きかけがあればそちら）
  const rowOf = (s) => {
    const d = drafts[s.id] || {};
    return {
      day: d.day ?? s.day_of_week,
      start: d.start ?? hm(s.start_time),
      end: d.end ?? hm(s.end_time),
      room: d.room ?? (s.room || ''),
      late: d.late ?? (s.late_threshold_minutes ?? 15),
    };
  };
  const isDirty = (s) => {
    const r = rowOf(s);
    return Number(r.day) !== Number(s.day_of_week) || r.start !== hm(s.start_time) || r.end !== hm(s.end_time)
      || (r.room || '') !== (s.room || '') || Number(r.late) !== Number(s.late_threshold_minutes ?? 15);
  };
  const setDraft = (s, patch) => setDrafts(v => ({ ...v, [s.id]: { ...rowOf(s), ...(v[s.id] || {}), ...patch } }));
  const pickPeriodForRow = (s, no) => {
    const p = periodTimes(no);
    if (p) setDraft(s, { start: p.startTime, end: p.endTime });
  };

  /** 1行ぶんを保存。changes は送る項目だけ */
  const put = async (id, changes) => {
    const res = await fetch('/api/schedule/schedules/' + id, { method: 'PUT', headers: auth(), body: JSON.stringify(changes) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error(d.message || '保存に失敗しました');
  };

  const saveRow = async (s) => {
    const r = rowOf(s);
    setBusy(true); setErr(''); setMsg('');
    try {
      await put(s.id, { day_of_week: Number(r.day), start_time: r.start, end_time: r.end,
                        room: r.room, late_threshold_minutes: Number(r.late) });
      setMsg(`${DAY_NAMES[r.day]}曜 ${r.start} を保存しました`);
      setEditingId(null);
      reload();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const delSchedule = async (s) => {
    if (!(await confirmDialog('この曜日時限を削除しますか？'))) return;
    setErr(''); setMsg('');
    try {
      const res = await fetch('/api/schedule/schedules/' + s.id, { method: 'DELETE', headers: auth() });
      const d   = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.message || '削除に失敗しました');
      setMsg('曜日時限を削除しました'); reload();
    } catch (e) { setErr(e.message); }
  };

  // まとめて変更: 選んだ行（選んでいなければ全部）に、入れた項目だけを反映して保存
  const targets = schedules.filter(s => picked.includes(s.id));
  const applyBulk = async () => {
    const changes = {};
    if (bulk.period) {
      const p = periodTimes(bulk.period);
      if (p) { changes.start_time = p.startTime; changes.end_time = p.endTime; }
    }
    if (bulk.clearRoom) changes.room = '';
    else if (bulk.room.trim()) changes.room = bulk.room.trim();
    if (bulk.late !== '' && !Number.isNaN(Number(bulk.late))) changes.late_threshold_minutes = Number(bulk.late);
    if (targets.length === 0) { setErr('変える曜日時限を選んでください'); return; }
    if (Object.keys(changes).length === 0) { setErr('変える項目を入れてください'); return; }
    if (!(await confirmDialog(`選んだ曜日時限（${targets.length}件）を変更します。よろしいですか？`))) return;
    setBusy(true); setErr(''); setMsg('');
    const failed = [];
    for (const s of targets) {
      try { await put(s.id, changes); }
      catch (e) { failed.push(`${DAY_NAMES[s.day_of_week]}曜 ${hm(s.start_time)}: ${e.message}`); }
    }
    setBusy(false);
    if (failed.length) setErr(failed.join(' / '));
    else setMsg(`${targets.length}件の曜日時限を変更しました`);
    setBulk({ period: '', room: '', clearRoom: false, late: '' });
    if (!failed.length) setPanel(null);
    reload();
  };

  const saveClassRoom = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await fetch('/api/schedule/classes/' + classId, {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({ name: cls && cls.name, semester: cls && cls.semester,
                               description: cls && cls.description, room: classRoom }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || '教室を保存できませんでした');
      setMsg(classRoom.trim() ? `授業の教室を「${classRoom.trim()}」にしました` : '授業の教室を消しました');
      setPanel(null);
      reload();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const addSchedule = async (e) => {
    e.preventDefault(); setErr(''); setMsg('');
    if (add.start >= add.end) { setErr('終了は開始より後の時刻にしてください'); return; }
    try {
      const res = await fetch('/api/schedule/classes/' + classId + '/schedules', {
        method: 'POST', headers: auth(),
        body: JSON.stringify({
          day_of_week: parseInt(add.day, 10), start_time: add.start, end_time: add.end,
          attendance_start_time: add.start, late_threshold_minutes: parseInt(add.late, 10), room: add.room,
          auto_attendance: autoState !== 'off',   // この授業の自動出席に合わせる
        })
      });
      const d = await res.json();
      if (!d.success && !d.id && !d.schedule) throw new Error(d.message || '追加に失敗しました');
      setMsg('曜日時限を追加しました'); setAdd(a => ({ ...a, room: '' })); setPanel(null); reload();
    } catch (e) { setErr(e.message); }
  };

  // 各回の出席人数（マトリクスから数える）
  const attendedCount = (sessionId) =>
    students.filter(stu => {
      const st = stu.cells[sessionId];
      return st === 'present' || st === 'late' || st === 'early_leave';
    }).length;

  const periodOptions = useMemo(() => periods.map(p => (
    <option key={p.periodNo} value={p.periodNo}>{p.label}（{p.startTime}〜{p.endTime}）</option>
  )), [periods]);

  const small = { display: 'block', fontSize: '0.8rem', color: '#4b5563' };
  const baseRoom = (cls && cls.room) || '';
  const periodLabel = (s) => {
    const no = periodOf(s.start_time, s.end_time);
    const p = periodTimes(no);
    return p ? p.label : '—';
  };
  const openBulk = () => { setPicked(schedules.map(s => s.id)); setPanel(panel === 'bulk' ? null : 'bulk'); setEditingId(null); };
  // この授業の自動出席: 'on'（全部オン）/ 'off'（全部オフ）/ 'partial'（詳細設定で一部だけ）
  const autoOn = schedules.filter(s => s.auto_attendance).length;
  const autoState = schedules.length === 0 || autoOn === 0 ? 'off' : autoOn === schedules.length ? 'on' : 'partial';

  /** 曜日時限の自動出席を変える。ids を省くと全部 */
  const setAuto = async (on, ids) => {
    const list = ids ? schedules.filter(s => ids.includes(s.id)) : schedules;
    setBusy(true); setErr(''); setMsg('');
    const failed = [];
    for (const s of list) {
      if (!!s.auto_attendance === on) continue;
      try { await put(s.id, { auto_attendance: on }); }
      catch (e) { failed.push(`${DAY_NAMES[s.day_of_week]}曜 ${hm(s.start_time)}: ${e.message}`); }
    }
    setBusy(false);
    if (failed.length) setErr(failed.join(' / '));
    else setMsg(ids ? `${DAY_NAMES[list[0].day_of_week]}曜 ${hm(list[0].start_time)} の自動出席を${on ? 'オン' : 'オフ'}にしました`
                    : `この授業の自動出席を${on ? 'オン' : 'オフ'}にしました`);
    reload();
  };

  // 自動開始をオンにした曜日時限があるのに、ほかの設定で止まっているか
  const anyAuto = schedules.some(s => s.auto_attendance);
  const autoBlocked = !!autoInfo && (autoInfo.systemEnabled === false || autoInfo.enabled === false);
  const box = { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.7rem 0.8rem', marginBottom: '0.8rem' };

  return (
    <>
      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, flex: 1 }}>曜日時限と教室</h2>
          {schedules.length > 1 && (
            <button className={panel === 'bulk' ? 'btn-primary' : 'btn-secondary'} onClick={openBulk}>まとめて変更…</button>
          )}
          <button className={panel === 'add' ? 'btn-primary' : 'btn-secondary'}
                  onClick={() => { setPanel(panel === 'add' ? null : 'add'); setEditingId(null); }}>＋ 曜日時限を追加</button>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
          この曜日と時刻をもとに、その日の授業（セッション）が立ちます。1つだけ直すときは行の「変更」、
          いくつか同時に直すときは「まとめて変更…」を使ってください。
        </p>

        {/* 自動開始が動かない理由【2026-09-28】 */}
        {anyAuto && autoBlocked && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.55rem 0.75rem',
                        marginBottom: '0.6rem', fontSize: '0.86rem', color: '#92400e' }}>
            {autoInfo.systemEnabled === false ? (
              <>「自動で開始」がオンの曜日時限がありますが、システム全体の自動出席が止まっているため、いまは自動で始まりません（教務の設定です）。</>
            ) : (
              <>
                「自動で開始」がオンの曜日時限がありますが、アカウント設定の自動出席がオフのため、いまは自動で始まりません。
                <button className="btn-secondary" disabled={busy} onClick={enableMyAuto}
                        style={{ marginLeft: '0.5rem', padding: '0.1rem 0.6rem', fontSize: '0.82rem' }}>
                  アカウント設定をオンにする
                </button>
              </>
            )}
          </div>
        )}

        {/* ── 現在の設定 ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                      padding: '0.5rem 0.7rem', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: '0.6rem' }}>
          {/* 【2026-10-07】教室が2か所にあって見分けにくい、と言われたので名前と説明をはっきりさせた */}
          <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>
            授業の教室<span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>（全曜日共通・名簿の取り込みで入る）</span>
          </span>
          {panel === 'room' ? (
            <>
              <input value={classRoom} onChange={e => setClassRoom(e.target.value)} placeholder="例: 深-22-203"
                     autoFocus style={{ minWidth: 180 }} />
              <button className="btn-primary" disabled={busy} onClick={saveClassRoom}>保存</button>
              <button className="btn-secondary" onClick={() => { setClassRoom(baseRoom); setPanel(null); }}>やめる</button>
            </>
          ) : (
            <>
              <strong style={{ fontSize: '1rem', color: baseRoom ? '#111827' : '#9ca3af' }}>{baseRoom || '未設定'}</strong>
              <button className="btn-secondary" style={{ padding: '0.15rem 0.6rem', fontSize: '0.82rem' }}
                      onClick={() => { setPanel('room'); setEditingId(null); }}>変更</button>
              <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                下の表で曜日時限ごとの教室を入れていない曜日は、この教室になります。曜日で教室が違うときだけ、下の表で入れてください
              </span>
            </>
          )}
        </div>

        {/* 自動で出席を開始（授業ごとにまとめて。詳細設定で特定の曜日時限だけ外せる） */}
        {schedules.length > 0 && (
          <div style={{ padding: '0.5rem 0.7rem', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>自動で出席を開始</span>
              <strong style={{ color: autoState === 'off' ? '#9ca3af' : autoBlocked ? '#b45309' : '#15803d' }}>
                {autoState === 'on' ? 'オン' : autoState === 'off' ? 'オフ' : `一部オン（${autoOn} / ${schedules.length}）`}
              </strong>
              {autoState !== 'on' && (
                <button className="btn-primary" disabled={busy} style={{ padding: '0.15rem 0.7rem', fontSize: '0.82rem' }}
                        onClick={async () => { if ((await confirmDialog('この授業の曜日時限をすべて、時刻になったら自動で出席の受付を始めるようにします。よろしいですか？'))) setAuto(true); }}>
                  オンにする
                </button>
              )}
              {autoState !== 'off' && (
                <button className="btn-secondary" disabled={busy} style={{ padding: '0.15rem 0.7rem', fontSize: '0.82rem' }}
                        onClick={() => setAuto(false)}>オフにする</button>
              )}
              <button type="button" onClick={() => setShowAutoDetail(v => !v)}
                      style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.82rem', padding: 0 }}>
                {showAutoDetail ? '詳細設定を閉じる' : '詳細設定…'}
              </button>
            </div>
            <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.2rem' }}>
              オンにすると、曜日時限の時刻になったとき、ボタンを押さなくても受付が始まり、終わりの時刻に閉じます。
            </div>
            {showAutoDetail && (
              <div style={{ marginTop: '0.5rem', borderTop: '1px dashed #e5e7eb', paddingTop: '0.45rem' }}>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.3rem' }}>
                  特定の曜日時限だけ自動で始めたくないときは、ここで外してください。
                </div>
                {schedules.map(s => (
                  <label key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                             marginRight: '1rem', fontSize: '0.88rem' }}>
                    <input type="checkbox" checked={!!s.auto_attendance} disabled={busy}
                           onChange={e => setAuto(e.target.checked, [s.id])} />
                    {DAY_NAMES[s.day_of_week]}曜 {periodLabel(s) !== '—' ? periodLabel(s) + ' ' : ''}{hm(s.start_time)}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {schedules.length === 0 ? (
          <>
            <p style={{ color: '#c2410c' }}>
              曜日時限が未設定です。設定しないと、この授業のセッションが自動で立ちません。「＋ 曜日時限を追加」から入れてください。
            </p>
            {/* 前の年度の同じ授業から写す【2026-11-03】（候補が無ければ何も出ない） */}
            <CopyFromPreviousCard classId={classId} onCopied={() => reload && reload()} />
          </>
        ) : (
          <div className="table-wrap">
            <table className="data-table" style={{ marginBottom: '0.8rem' }}>
              <thead><tr>
                <th>曜日</th><th>時限</th><th>時刻</th><th>教室</th><th>遅刻判定</th>
                <th title="その時刻になったら、ボタンを押さなくても出席の受付を始める">自動で開始</th>
                <th style={{ width: 150 }}></th>
              </tr></thead>
              <tbody>{schedules.map(s => {
                if (editingId === s.id) {
                  const r = rowOf(s);
                  const pno = periodOf(r.start, r.end);
                  return (
                    <tr key={s.id} style={{ background: '#fffbeb' }}>
                      <td>
                        <select value={r.day} onChange={e => setDraft(s, { day: Number(e.target.value) })}>
                          {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}曜</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={pno} onChange={e => pickPeriodForRow(s, e.target.value)}>
                          <option value="">{pno ? '' : '時刻を直接'}</option>
                          {periodOptions}
                        </select>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <input type="time" value={r.start} onChange={e => setDraft(s, { start: e.target.value })} />
                        〜
                        <input type="time" value={r.end} onChange={e => setDraft(s, { end: e.target.value })} />
                      </td>
                      <td>
                        <input value={r.room} onChange={e => setDraft(s, { room: e.target.value })}
                               placeholder={baseRoom ? `空なら ${baseRoom}` : '教室'} style={{ width: 140 }} />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <input type="number" value={r.late} onChange={e => setDraft(s, { late: e.target.value })}
                               style={{ width: 64 }} />分
                      </td>
                      <td style={{ color: '#6b7280', fontSize: '0.82rem' }}>上の「自動で出席を開始」で設定</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn-primary" disabled={busy || !isDirty(s)} onClick={() => saveRow(s)}
                                style={{ marginRight: '0.3rem' }}>保存</button>
                        <button className="btn-secondary" disabled={busy}
                                onClick={() => { setDrafts(v => { const x = { ...v }; delete x[s.id]; return x; }); setEditingId(null); }}>
                          やめる
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 700 }}>{DAY_NAMES[s.day_of_week]}曜</td>
                    <td>{periodLabel(s)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{hm(s.start_time)}〜{hm(s.end_time)}</td>
                    <td>
                      {s.room
                        ? <span>
                            <strong>{s.room}</strong>
                            <span style={{ marginLeft: '0.3rem', fontSize: '0.7rem', background: '#dbeafe', color: '#1e40af',
                                           borderRadius: 999, padding: '0 0.4rem', whiteSpace: 'nowrap' }}
                                  title="この曜日時限だけの教室（授業の教室より優先）">この曜日時限</span>
                          </span>
                        : baseRoom
                          ? <span style={{ color: '#6b7280' }} title="この曜日時限には教室を入れていないので、授業の教室を使います">
                              {baseRoom}
                              <span style={{ marginLeft: '0.3rem', fontSize: '0.7rem', background: '#f3f4f6', color: '#4b5563',
                                             borderRadius: 999, padding: '0 0.4rem', whiteSpace: 'nowrap' }}>授業の教室</span>
                            </span>
                          : <span style={{ color: '#9ca3af' }}>未設定</span>}
                    </td>
                    <td>{s.late_threshold_minutes ?? 15}分</td>
                    <td>
                      {s.auto_attendance
                        ? <span style={{ color: autoBlocked ? '#b45309' : '#15803d', fontWeight: 700 }}
                                title={autoBlocked ? '下の案内の理由で、いまは自動で始まりません' : '時刻になると自動で受付を始めます'}>
                            オン{autoBlocked ? '（止まっています）' : ''}
                          </span>
                        : <span style={{ color: '#9ca3af' }}>オフ</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-secondary" style={{ marginRight: '0.3rem' }}
                              onClick={() => { setEditingId(s.id); setPanel(null); }}>変更</button>
                      <button className="btn-danger" onClick={() => delSchedule(s)}>削除</button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}

        {/* ── まとめて変更 ── */}
        {panel === 'bulk' && (
          <div style={box}>
            <strong style={{ display: 'block', marginBottom: '0.4rem' }}>まとめて変更</strong>
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '0.5rem', fontSize: '0.88rem' }}>
              <span style={{ color: '#4b5563' }}>変える曜日時限：</span>
              {schedules.map(s => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <input type="checkbox" checked={picked.includes(s.id)}
                         onChange={() => setPicked(v => (v.includes(s.id) ? v.filter(x => x !== s.id) : [...v, s.id]))} />
                  {DAY_NAMES[s.day_of_week]}曜 {hm(s.start_time)}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
              <div>
                <label style={small}>時限</label>
                <select value={bulk.period} onChange={e => setBulk(b => ({ ...b, period: e.target.value }))}>
                  <option value="">変えない</option>
                  {periodOptions}
                </select>
              </div>
              <div>
                <label style={small}>教室</label>
                <input value={bulk.room} disabled={bulk.clearRoom} placeholder="変えない"
                       onChange={e => setBulk(b => ({ ...b, room: e.target.value }))} style={{ width: 150 }} />
              </div>
              <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem', alignSelf: 'center' }}>
                <input type="checkbox" checked={bulk.clearRoom}
                       onChange={e => setBulk(b => ({ ...b, clearRoom: e.target.checked }))} />
                教室を授業の教室に戻す
              </label>
              <div>
                <label style={small}>遅刻判定（分）</label>
                <input type="number" value={bulk.late} placeholder="変えない"
                       onChange={e => setBulk(b => ({ ...b, late: e.target.value }))} style={{ width: 90 }} />
              </div>
              <button className="btn-primary" disabled={busy || picked.length === 0} onClick={applyBulk}>
                {picked.length}件に反映して保存
              </button>
              <button className="btn-secondary" onClick={() => setPanel(null)}>閉じる</button>
            </div>
          </div>
        )}

        {/* ── 追加 ── */}
        {panel === 'add' && (
          <form onSubmit={addSchedule} style={{ ...box, display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'end' }}>
            <strong style={{ width: '100%' }}>曜日時限を追加</strong>
            <div>
              <label style={small}>曜日</label>
              <select value={add.day} onChange={e => setAdd(a => ({ ...a, day: e.target.value }))}>
                {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}曜</option>)}
              </select>
            </div>
            <div>
              <label style={small}>時限</label>
              <select value={add.period} onChange={e => {
                const p = periodTimes(e.target.value);
                setAdd(a => ({ ...a, period: e.target.value, ...(p ? { start: p.startTime, end: p.endTime } : {}) }));
              }}>
                <option value="">時刻を直接</option>
                {periodOptions}
              </select>
            </div>
            <div>
              <label style={small}>開始</label>
              <input type="time" value={add.start} onChange={e => setAdd(a => ({ ...a, start: e.target.value, period: '' }))} />
            </div>
            <div>
              <label style={small}>終了</label>
              <input type="time" value={add.end} onChange={e => setAdd(a => ({ ...a, end: e.target.value, period: '' }))} />
            </div>
            <div>
              <label style={small}>この曜日時限の教室（空なら授業の教室）</label>
              <input value={add.room} onChange={e => setAdd(a => ({ ...a, room: e.target.value }))} style={{ width: 140 }} />
            </div>
            <div>
              <label style={small}>遅刻判定（分）</label>
              <input type="number" value={add.late} onChange={e => setAdd(a => ({ ...a, late: e.target.value }))} style={{ width: 90 }} />
            </div>
            <span style={{ fontSize: '0.8rem', color: '#6b7280', alignSelf: 'center' }}>
              自動で出席を開始: {autoState === 'off' ? 'オフ' : 'オン'}（この授業の設定に合わせます）
            </span>
            <button className="btn-primary" type="submit">追加</button>
            <button className="btn-secondary" type="button" onClick={() => setPanel(null)}>閉じる</button>
          </form>
        )}
      </div>

      {/* この先の授業（予定の回・休講・代講）【2026-09-28】曜日時限が変わると読み直す */}
      <UpcomingSessions classId={classId} auth={auth} setErr={setErr} setMsg={setMsg}
                        reloadKey={schedules.map(s => `${s.id}:${s.day_of_week}:${hm(s.start_time)}:${s.room || ''}:${s.auto_attendance ? 1 : 0}`).join('|')} />

      <div className="card">
        <div className="card-header"><h2>実施した回（{sessions.length}）</h2></div>
        {sessions.length === 0 ? (
          <p style={{ color: '#888' }}>まだ授業を実施していません。</p>
        ) : (
          <table className="data-table">
            <thead><tr><th>回</th><th>日付</th><th>出席人数</th></tr></thead>
            <tbody>{sessions.map(s => (
              <tr key={s.id}>
                <td>第{s.no}回</td>
                <td>{(() => {
                  const x = new Date(s.date);
                  if (isNaN(x.getTime())) return String(s.date).slice(0, 10);
                  const p = (n) => String(n).padStart(2, '0');
                  return /^\d{4}-\d{2}-\d{2}$/.test(String(s.date)) ? s.date
                    : `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
                })()}</td>
                <td>{attendedCount(s.id)} / {students.length}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </>
  );
}
